import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ErrorFallback } from './ErrorFallback';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Top-level error boundary.
 *
 * Still a class component because React has no hook equivalent of
 * `componentDidCatch`. It deliberately does not ship the error to a third-party
 * service: in this app an error payload can contain an amount, a wallet address
 * or a token, and that decision needs a conscious owner, not a default.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Message and component stack only — never the error object wholesale.
    console.error('[novix] render error', error.message, info.componentStack);
  }

  private readonly reset = () => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (this.state.error !== null) {
      return this.props.fallback ?? <ErrorFallback onReset={this.reset} />;
    }
    return this.props.children;
  }
}
